/**
 * Client-side copy + off-topic detection + message parsing for the
 * SEO Intelligence Help Assistant. Kept in sync with the backend mirror
 * (backend/src/lib/help-assistant/help-responses.js).
 *
 * Running the off-topic gate on the client lets us answer small-talk
 * instantly without a network round-trip, exactly like the Rivera pattern.
 */

export const CONTACT_EMAIL = 'gaurav@digitalpresencematters.com';

export const HELP_OFF_TOPIC_RESPONSE = `Please ask a question about SEO Intelligence.

I can help you with My Workspace, onboarding, pending work, tasks, tools, and settings. Try asking how to start your day, update a task, or raise a support ticket.`;

export const HELP_UNAVAILABLE_RESPONSE = `Please ask a question about SEO Intelligence.

I can help you with My Workspace, onboarding, pending work, tasks, tools, and settings. If you need more help, contact ${CONTACT_EMAIL}.`;

// Paths the assistant is allowed to link to (mirrors knowledge-base navigation_map).
const KNOWN_PATHS = new Set([
  '/documents',
  '/playbook',
  '/dashboard',
  '/workspace',
  '/clients',
  '/clients/readiness',
  '/reports',
  '/stages',
  '/kanban',
  '/tasks',
  '/tools',
  '/tool-catalog',
  '/tool-runs',
  '/requests',
  '/activity-analysis',
  '/tools/technical-seo-audit',
  '/tools/keyword-tracking',
  '/tools/gbp-audit',
  '/content',
  '/tools/doc-to-html',
  '/tools/blog',
  '/leads',
  '/campaigns',
  '/market-intel',
  '/ads-audit',
  '/lead-iq',
  '/meta-creatives',
  '/edit-studio',
  '/ad-safe',
  '/tools/ghl',
  '/tools/ghl-attribution',
  '/client-onboarding',
  '/client-onboarding/my-queue',
  '/pending-work',
  '/approvals',
  '/service-plan',
  '/monthly-budgets',
  '/teams',
  '/my-team',
  '/support',
  '/support-center',
  '/inbox',
  '/settings',
  '/settings/people',
  '/settings/security',
  '/settings/notifications',
  '/settings/api-keys',
  '/settings/prompt-templates',
  '/settings/notification-rules',
  '/settings/auto-generation',
  '/settings/tool-access',
  '/login',
  '/reset-password',
]);

const SEO_KEYWORDS = [
  'seo', 'seo intelligence', 'dashboard', 'metric', 'metrics', 'overview',
  'workspace', 'my workspace', 'work queue',
  // "client" is kept alongside "location": the UI says Location now, but people
  // still type the old word, and these are matched against user input.
  'client', 'clients', 'add client', 'create client',
  'location', 'locations', 'add location', 'create location',
  'practice', 'practices', 'add practice',
  'onboarding', 'pending work', 'readiness', 'service plan', 'approvals',
  'manager', 'serpstat',
  'command center', 'archive', 'audit', 'pipeline',
  'report', 'reports', 'technical', 'keyword', 'keywords', 'gbp',
  'google business', 'opportunity', 'generate', 'regenerate', 'distribute',
  'export', 'excel', 'xlsx', 'findings', 'preflight', 'retry',
  'task', 'tasks', 'task list', 'kanban', 'board', 'column', 'columns',
  'status', 'priority', 'proof', 'comment', 'steps', 'backlog', 'review',
  'tool', 'tools', 'blog', 'content', 'post', 'paa', 'people also ask',
  'draft', 'approve', 'reject', 'submit', 'exported',
  'team', 'my team', 'teams', 'people', 'user', 'users', 'department', 'departments',
  'invite', 'invitation', 'resend', 'bulk import', 'audit log', 'role', 'roles',
  'permission', 'permissions', 'admin', 'team member', 'team leader',
  'settings', 'api key', 'api keys', 'credential', 'credentials',
  'prompt', 'prompt template', 'notification', 'notifications',
  'notification rule', 'google chat', 'chat bot', 'smtp', 'email',
  'auto generation', 'schedule', 'login', 'log in', 'logout', 'log out',
  'sign in', 'sign out', 'password', 'reset', 'security', 'account',
  'navigate', 'navigation', 'route', 'page', 'module', 'how to', 'guide',
  'getting started', 'troubleshoot',
];

const OFF_TOPIC_PATTERNS = [
  /^(hi+|hey+|hello+|yo+|sup+|howdy)\b[!.,?\s]*$/i,
  /^how are you\b/i,
  /^how'?s it going\b/i,
  /^what'?s up\b/i,
  /^good (morning|afternoon|evening|night)\b/i,
  /^thanks?[!.,?\s]*$/i,
  /^thank you[!.,?\s]*$/i,
  /^ok(ay)?[!.,?\s]*$/i,
  /^cool[!.,?\s]*$/i,
  /^nice[!.,?\s]*$/i,
  /^lol+[!.,?\s]*$/i,
  /^haha+[!.,?\s]*$/i,
  /^test[!.,?\s]*$/i,
  /^asdf+$/i,
  /^[aeiouh]+$/i,
  /^who are you\b/i,
  /^what are you\b/i,
  /^tell me (a )?joke\b/i,
  /^weather\b/i,
  /^i love you\b/i,
  /^are you (a )?(bot|ai|human)\b/i,
];

function editDistance(a, b) {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function fuzzyMatchesKeyword(word, keyword) {
  if (word === keyword) return true;
  if (word.length < 3) return false;
  const maxDist = keyword.length <= 4 ? 1 : 2;
  return editDistance(word, keyword) <= maxDist;
}

function hasSeoKeyword(text) {
  if (SEO_KEYWORDS.some((kw) => text.includes(kw))) return true;
  const words = text.split(/\s+/);
  return words.some((word) => SEO_KEYWORDS.some((kw) => fuzzyMatchesKeyword(word, kw)));
}

function looksLikeQuestion(text) {
  if (text.includes('?')) return true;
  return /^(how|what|where|when|why|can|do|does|is|are|which|help)\b/.test(text);
}

/** True when the message is plausibly about SEO Intelligence. */
export function isSeoIntelligenceQuestion(text) {
  const t = String(text || '').trim().toLowerCase();
  if (!t) return false;
  if (OFF_TOPIC_PATTERNS.some((p) => p.test(t))) return false;
  if (hasSeoKeyword(t)) return true;
  if (looksLikeQuestion(t) && t.length >= 10) return true;
  return false;
}

/**
 * Parse assistant text into renderable segments:
 *   { type: 'text',  value }
 *   { type: 'link',  label, path }   — from [[Label|/path]] (path must be known)
 *   { type: 'email', value }          — bare email addresses (rendered as mailto)
 */
export function parseHelpMessageSegments(content) {
  if (!content) return [];

  const matches = [];

  // [[Label|/path]]
  const bracketRe = /\[\[([^|\]]+)\|(\/[a-zA-Z0-9/_-]+)\]\]/g;
  let m;
  while ((m = bracketRe.exec(content)) !== null) {
    const path = m[2].trim();
    if (!KNOWN_PATHS.has(path)) continue;
    matches.push({ start: m.index, end: m.index + m[0].length, kind: 'link', label: m[1].trim(), path });
  }

  // bare email addresses
  const emailRe = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  while ((m = emailRe.exec(content)) !== null) {
    const overlaps = matches.some((x) => m.index >= x.start && m.index < x.end);
    if (overlaps) continue;
    matches.push({ start: m.index, end: m.index + m[0].length, kind: 'email', value: m[0] });
  }

  matches.sort((a, b) => a.start - b.start);

  const segments = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start < cursor) continue;
    if (match.start > cursor) {
      segments.push({ type: 'text', value: content.slice(cursor, match.start) });
    }
    if (match.kind === 'link') {
      segments.push({ type: 'link', label: match.label, path: match.path });
    } else {
      segments.push({ type: 'email', value: match.value });
    }
    cursor = match.end;
  }
  if (cursor < content.length) {
    segments.push({ type: 'text', value: content.slice(cursor) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', value: content }];
}
